import React, { useState, useEffect } from 'react';
import { Tag, Plus, Pencil, Trash2, X, Check } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAG_COLORS = [
  { value: 'blue', label: 'Blå', class: 'bg-blue-400' },
  { value: 'yellow', label: 'Gul', class: 'bg-yellow-400' },
  { value: 'pink', label: 'Rosa', class: 'bg-pink-400' },
  { value: 'emerald', label: 'Grön', class: 'bg-emerald-400' },
  { value: 'purple', label: 'Lila', class: 'bg-purple-400' },
  { value: 'orange', label: 'Orange', class: 'bg-orange-400' },
];

export function TagManager({ tags, onTagsChange }) {
  const { currentWorkspace } = useWorkspace();
  const [isOpen, setIsOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('blue');
  const [editingTag, setEditingTag] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCreateTag = async () => {
    if (!newTagName.trim() || !currentWorkspace) return;

    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/tags/${currentWorkspace.id}`,
        { name: newTagName.trim(), color: newTagColor },
        { withCredentials: true }
      );
      onTagsChange([...tags, data]);
      setNewTagName('');
      setNewTagColor('blue');
      toast.success('Tagg skapad');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte skapa tagg');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTag = async (tagId, updates) => {
    setLoading(true);
    try {
      const { data } = await axios.patch(
        `${API}/tags/${tagId}`,
        updates,
        { withCredentials: true }
      );
      onTagsChange(tags.map(t => t.id === tagId ? data : t));
      setEditingTag(null);
      toast.success('Tagg uppdaterad');
    } catch (error) {
      toast.error('Kunde inte uppdatera tagg');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!window.confirm('Är du säker på att du vill ta bort denna tagg?')) return;

    setLoading(true);
    try {
      await axios.delete(`${API}/tags/${tagId}`, { withCredentials: true });
      onTagsChange(tags.filter(t => t.id !== tagId));
      toast.success('Tagg borttagen');
    } catch (error) {
      toast.error('Kunde inte ta bort tagg');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-white/10 text-zinc-400 hover:bg-white/5 hover:text-zinc-100 h-8"
          data-testid="manage-tags-btn"
        >
          <Tag className="w-3.5 h-3.5 mr-1.5" />
          Hantera taggar
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#141416] border-white/10 text-zinc-50 max-w-md" data-testid="tag-manager-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-medium font-['Outfit'] flex items-center gap-2">
            <Tag className="w-5 h-5" />
            Hantera taggar
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-4">
          {/* Create New Tag */}
          <div className="space-y-3">
            <Label className="text-zinc-400 text-sm">Skapa ny tagg</Label>
            <div className="flex gap-2">
              <Input
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="Taggnamn"
                className="bg-[#09090B] border-white/10 text-zinc-100 flex-1"
                data-testid="new-tag-name-input"
              />
              <div className="flex gap-1">
                {TAG_COLORS.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => setNewTagColor(color.value)}
                    className={`w-7 h-7 rounded-md ${color.class} transition-all ${
                      newTagColor === color.value ? 'ring-2 ring-white ring-offset-2 ring-offset-[#141416]' : 'opacity-50 hover:opacity-75'
                    }`}
                    title={color.label}
                  />
                ))}
              </div>
              <Button
                onClick={handleCreateTag}
                disabled={loading || !newTagName.trim()}
                size="icon"
                className="bg-zinc-100 text-zinc-900 hover:bg-white h-9 w-9"
                data-testid="create-tag-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Existing Tags */}
          <div className="space-y-3">
            <Label className="text-zinc-400 text-sm">Befintliga taggar ({tags.length})</Label>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {tags.length === 0 ? (
                <p className="text-sm text-zinc-600 text-center py-4">
                  Inga taggar skapade ännu
                </p>
              ) : (
                tags.map((tag) => (
                  <TagItem
                    key={tag.id}
                    tag={tag}
                    isEditing={editingTag === tag.id}
                    onEdit={() => setEditingTag(tag.id)}
                    onCancelEdit={() => setEditingTag(null)}
                    onUpdate={(updates) => handleUpdateTag(tag.id, updates)}
                    onDelete={() => handleDeleteTag(tag.id)}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TagItem({ tag, isEditing, onEdit, onCancelEdit, onUpdate, onDelete }) {
  const [name, setName] = useState(tag.name);
  const [color, setColor] = useState(tag.color);

  useEffect(() => {
    if (isEditing) {
      setName(tag.name);
      setColor(tag.color);
    }
  }, [isEditing, tag]);

  const handleSave = () => {
    if (!name.trim()) return;
    onUpdate({ name: name.trim(), color });
  };

  const colorClass = TAG_COLORS.find(c => c.value === tag.color)?.class || 'bg-blue-400';

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 p-2 rounded-lg bg-[#09090B] border border-white/10">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="bg-transparent border-0 text-zinc-100 h-7 p-0 focus:ring-0"
          autoFocus
        />
        <div className="flex gap-1">
          {TAG_COLORS.map((c) => (
            <button
              key={c.value}
              onClick={() => setColor(c.value)}
              className={`w-5 h-5 rounded ${c.class} transition-all ${
                color === c.value ? 'ring-1 ring-white' : 'opacity-50'
              }`}
            />
          ))}
        </div>
        <button
          onClick={handleSave}
          className="p-1 rounded hover:bg-white/10 text-emerald-400"
        >
          <Check className="w-4 h-4" />
        </button>
        <button
          onClick={onCancelEdit}
          className="p-1 rounded hover:bg-white/10 text-zinc-500"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 p-2 rounded-lg bg-[#09090B] border border-white/10 group">
      <div className={`w-3 h-3 rounded-full ${colorClass}`} />
      <span className="flex-1 text-sm text-zinc-100">{tag.name}</span>
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={onEdit}
          className="p-1 rounded hover:bg-white/10 text-zinc-500 hover:text-zinc-300"
          data-testid={`edit-tag-${tag.id}`}
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onDelete}
          className="p-1 rounded hover:bg-white/10 text-zinc-500 hover:text-red-400"
          data-testid={`delete-tag-${tag.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
